/** @jsxImportSource @emotion/react */
import { useState, useRef, useLayoutEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog } from "primereact/dialog";
import { Boton } from "../../shared";

function nombrePersona(persona) {
  if (!persona) return "";
  return (
    [persona.nombre, persona.apellido].filter(Boolean).join(" ") ||
    persona.email
  );
}

function recorteDeLineas(lineas) {
  return {
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: lineas,
    WebkitBoxOrient: "vertical"
  };
}

// Tarjeta compacta de un destacado personal. Es una sola para todos los tipos:
// la API ya la trae resuelta (título, subtítulo, estado y link). Los textos se
// recortan para que las tarjetas tengan un alto parejo; si alguno no entra,
// aparece "Ver más" con el contenido completo en un diálogo.
export default function TarjetaDestacado({
  destacado,
  onDescartar,
  onAlternarPrioritario
}) {
  const navigate = useNavigate();
  const [truncada, setTruncada] = useState(false);
  const [verCompleta, setVerCompleta] = useState(false);
  const tarjetaRef = useRef(null);
  const tituloRef = useRef(null);
  const subtituloRef = useRef(null);
  const notaRef = useRef(null);

  // Mide si algún texto quedó recortado. Se vuelve a medir cuando cambia el
  // ancho de la tarjeta (ventana, panel del asistente).
  useLayoutEffect(() => {
    const medir = () => {
      const recortado = [tituloRef, subtituloRef, notaRef].some(
        ({ current: el }) =>
          el &&
          (el.scrollHeight > el.clientHeight + 1 ||
            el.scrollWidth > el.clientWidth + 1)
      );
      setTruncada(recortado);
    };
    medir();
    const observador = new ResizeObserver(medir);
    if (tarjetaRef.current) observador.observe(tarjetaRef.current);
    return () => observador.disconnect();
  }, [destacado.titulo, destacado.subtitulo, destacado.nota]);

  function abrir(event) {
    if (!destacado.link) return;
    if (event.metaKey || event.ctrlKey) {
      window.open(destacado.link, "_blank");
    } else {
      navigate(destacado.link);
    }
  }

  return (
    <div className="col-12 md:col-6 lg:col-4 xl:col-3">
      <div
        ref={tarjetaRef}
        onClick={abrir}
        css={{
          // Los botones aparecen al pasar el mouse por la tarjeta (o al
          // enfocarlos con el teclado). En pantallas táctiles, sin hover,
          // quedan siempre a la vista.
          "@media (hover: hover)": {
            "& .accionesTarjeta": { opacity: 0, transition: "opacity 0.15s" },
            "&:hover .accionesTarjeta, & .accionesTarjeta:focus-within": {
              opacity: 1
            }
          },
          height: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          padding: "10px 12px",
          borderRadius: "10px",
          backgroundColor: "white",
          // El borde está siempre para que la estrella no mueva el contenido
          border: `1px solid ${
            destacado.prioritario ? "var(--amarillo)" : "transparent"
          }`,
          boxShadow: destacado.prioritario
            ? "0 0 10px rgba(239, 163, 32, 0.55)"
            : "0 1px 4px rgba(0, 0, 0, 0.12)",
          cursor: destacado.link ? "pointer" : "default",
          transition: "box-shadow 0.15s, border-color 0.15s",
          "&:hover": destacado.link
            ? {
                boxShadow: destacado.prioritario
                  ? "0 0 14px rgba(239, 163, 32, 0.75)"
                  : "0 3px 10px rgba(0, 0, 0, 0.18)"
              }
            : {}
        }}
      >
        <div
          css={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "6px",
            fontSize: "0.75rem",
            color: "var(--grisTextos)",
            textTransform: "uppercase",
            "& .p-button": { width: "1.8rem", height: "1.8rem", padding: 0 }
          }}
        >
          <span>
            <i className={destacado.icono} css={{ marginRight: "0.4em" }}></i>
            {destacado.etiquetaTipo}
          </span>
          {/* Los clicks de los botones no tienen que abrir la tarjeta */}
          <span
            className="accionesTarjeta"
            onClick={(e) => e.stopPropagation()}
            css={{ display: "flex", alignItems: "center", gap: "2px" }}
          >
            <Boton
              icono
              amarillo={destacado.prioritario}
              callback={() => onAlternarPrioritario(destacado.id)}
            >
              <i
                className={
                  destacado.prioritario ? "pi pi-star-fill" : "pi pi-star"
                }
              ></i>
            </Boton>
            <Boton
              icono
              confirmacion="¿Eliminar este destacado?"
              callback={() => onDescartar(destacado.id)}
            >
              <i className="pi pi-times"></i>
            </Boton>
          </span>
        </div>

        <strong ref={tituloRef} css={recorteDeLineas(2)}>
          {destacado.titulo}
        </strong>

        {destacado.subtitulo && (
          <div
            ref={subtituloRef}
            css={{
              fontSize: "0.85rem",
              color: "var(--grisTextos)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}
          >
            {destacado.subtitulo}
          </div>
        )}

        {destacado.estado && (
          <div>
            <span
              css={{
                display: "inline-block",
                fontSize: "0.75rem",
                padding: "1px 8px",
                borderRadius: "10px",
                backgroundColor: "#eef2f6"
              }}
            >
              {destacado.estado}
            </span>
          </div>
        )}

        {destacado.nota && (
          <div
            ref={notaRef}
            css={{
              fontSize: "0.85rem",
              fontStyle: "italic",
              whiteSpace: "pre-line",
              ...recorteDeLineas(3)
            }}
          >
            {destacado.nota}
          </div>
        )}

        {destacado.autor && (
          <div css={{ fontSize: "0.75rem", color: "var(--grisTextos)" }}>
            De {nombrePersona(destacado.autor)}
          </div>
        )}

        {truncada && (
          // El click de "Ver más" no tiene que abrir la tarjeta
          <div
            className="accionesTarjeta"
            onClick={(e) => e.stopPropagation()}
            css={{
              marginTop: "auto",
              "& .p-button": { padding: "0.2em 0", fontSize: "0.8rem" }
            }}
          >
            <Boton texto chico callback={() => setVerCompleta(true)}>
              Ver más
            </Boton>
          </div>
        )}
      </div>

      <Dialog
        header={destacado.etiquetaTipo}
        visible={verCompleta}
        onHide={() => setVerCompleta(false)}
        style={{ width: "32rem" }}
        breakpoints={{ "641px": "95vw" }}
      >
        <div css={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <strong>{destacado.titulo}</strong>
          {destacado.subtitulo && (
            <div css={{ color: "var(--grisTextos)" }}>
              {destacado.subtitulo}
            </div>
          )}
          {destacado.estado && <div>Estado: {destacado.estado}</div>}
          {destacado.nota && (
            <div css={{ whiteSpace: "pre-line" }}>{destacado.nota}</div>
          )}
          {destacado.autor && (
            <div css={{ fontSize: "0.85rem", color: "var(--grisTextos)" }}>
              De {nombrePersona(destacado.autor)}
            </div>
          )}
          {destacado.link && (
            <div css={{ marginTop: "8px" }}>
              <Boton uri={destacado.link}>Abrir</Boton>
            </div>
          )}
        </div>
      </Dialog>
    </div>
  );
}
